import Vue from 'vue';

const types = ['info', 'warning', 'danger', 'success'];

// Copy plain content at enqueue time; record names are always rendered as text.
function normalizeContent(content) {
  if (!Array.isArray(content)) return [];
  const text = value => ['string', 'number'].includes(typeof value) ? String(value) : '';
  return content.filter(block => block && ['record', 'section', 'note', 'question'].includes(block.type)).map(block => ({
    type: block.type,
    title: text(block.title),
    text: text(block.text),
    groups: (Array.isArray(block.groups) ? block.groups : []).filter(group => group && typeof group === 'object').map(group => ({
      title: text(group.title),
      count: Number.isSafeInteger(group.count) && group.count >= 0 ? group.count : null,
      items: (Array.isArray(group.items) ? group.items : []).map(text).filter(Boolean),
      more: text(group.more),
    })),
  }));
}

export function createConfirmService({ onError = error => console.error(error) } = {}) {
  const state = Vue.observable({ current: null });
  let sequence = 0;
  let active = null;
  let queue = [];
  let advancing = false;

  const advance = () => {
    if (active || advancing || !queue.length) return;
    active = queue.shift();
    state.current = active.dialog;
  };
  const scheduleAdvance = () => {
    if (advancing) return;
    advancing = true;
    Vue.nextTick(() => { advancing = false; advance(); });
  };
  const complete = (request, confirmed) => {
    request.resolve(confirmed);
    if (request.callback) {
      try { Promise.resolve(request.callback(confirmed)).catch(onError); }
      catch (error) { onError(error); }
    }
  };

  function confirm(options) {
    if (!options || typeof options !== 'object' || Array.isArray(options)) {
      throw new TypeError('Confirmation options must be an object.');
    }
    if (options.callback !== undefined && typeof options.callback !== 'function') {
      throw new TypeError('Confirmation callback must be a function.');
    }
    const type = types.includes(options.type) ? options.type : 'info';
    const dialog = {
      id: ++sequence,
      type,
      title: String(options.title || ''),
      message: String(options.message || ''),
      content: normalizeContent(options.content),
      button: { ...options.button },
      dismissOnBackdrop: options.dismissOnBackdrop ?? ['info', 'success'].includes(type),
    };
    return new Promise(resolve => {
      queue.push({ dialog, resolve, callback: options.callback });
      advance();
    });
  }

  const answer = (id, confirmed) => {
    if (!active || active.dialog.id !== id) return;
    const request = active;
    active = null;
    state.current = null;
    scheduleAdvance();
    complete(request, Boolean(confirmed));
  };
  confirm.close = () => { if (active) answer(active.dialog.id, false); };
  confirm.closeAll = () => {
    const pending = active ? [active, ...queue] : queue;
    queue = [];
    active = null;
    state.current = null;
    scheduleAdvance();
    pending.forEach(request => complete(request, false));
  };

  return { state, confirm, answer };
}

export const confirmService = createConfirmService({
  onError(error) {
    if (Vue.config.errorHandler) Vue.config.errorHandler(error, null, 'confirmation callback');
    else console.error(error);
  },
});
